import { Grid, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MuiTelInput } from 'mui-tel-input';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';

import BoxIcon from '@/assets/icons/fulfill/ilustracion_caja.svg';
import Button from '@/components/Button';
import useResponsive from '@/hooks/useResponsive';

import {
  ALLOWED_COUNTRIES,
  CountryCode,
  DEFAULT_COUNTRY,
  MAX_PHONE_LENGTH_BY_COUNTRY,
  SendToWarehouseFormData,
} from '../../inventorySchema';

const SHIPMENT_TYPE_OPTIONS = [
  { value: 'SPT', label: 'Sprint' },
  { value: 'SMD', label: 'Same Day' },
];

interface LabelYourProductsProps {
  step: number;
  setCurrentStep: (step: number) => void;
  register: UseFormRegister<SendToWarehouseFormData>;
  errors: FieldErrors<SendToWarehouseFormData>;
  isDisabled: boolean;
  control: Control<SendToWarehouseFormData>;
  handleChangeSelectedCountryPhone?: (country: CountryCode) => void;
  requiresHomePickup?: boolean;
  editStep?: boolean;
  defaultCountryPhone?: CountryCode;
}
const LabelYourProducts = ({
  step,
  setCurrentStep,
  register,
  errors,
  isDisabled,
  control,
  handleChangeSelectedCountryPhone,
  requiresHomePickup,
  editStep = false,
  defaultCountryPhone,
}: LabelYourProductsProps) => {
  const theme = useTheme();
  const isResponsive = useResponsive('down', 'xl');
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isResponsive ? '1rem' : 0,
          flexDirection: isResponsive ? 'column' : 'row',
          margin: '1rem 0',
        }}
      >
        <Typography variant="subtitle1" sx={{ minWidth: '4.1rem' }}>
          Paso <span style={{ color: theme.palette.primary.main }}>3</span>
        </Typography>
        <Divider
          orientation={isResponsive ? 'horizontal' : 'vertical'}
          flexItem
          sx={{
            mx: isResponsive ? 0 : 2,
          }}
        />
        <Typography variant="subtitle2">Etiquetado de productos recomendados</Typography>
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row', lg: 'column', xl: 'row' },
          alignItems: 'flex-start',
          gap: '1.5rem',
        }}
      >
        <img src={BoxIcon} alt="Box" />
        <Box color={theme.palette.grey[600]}>
          <Typography variant="subtitle2" mb={2}>
            Instrucciones para etiquetar correctamente
          </Typography>
          <ul style={{ paddingLeft: '1.3rem', lineHeight: '1.5' }}>
            <li>
              Las etiquetas tienen el{' '}
              <span style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
                SKU fulfill99
              </span>{' '}
              para tu producto, pega la indicada en cada uno sin tapar las especificaciones.
            </li>
            <li>Estas deben quedar visibles para poder ser escaneadas.</li>
            <li>
              Para que podamos ingresar tus productos a nuestra bodega, todos los bultos que envíes
              deben estar correctamente preparados e identificados con nuestras etiquetas: por
              producto y por bulto.
            </li>
            <li>
              Agrupa tus productos en bultos, recuerda indicar la cantidad total de los mismos.
            </li>
            <li>Los productos grandes podrás enviarlos en su empaque original.</li>
          </ul>
        </Box>
      </Paper>

      <Box mt={2}>
        <Typography variant="subtitle2" mb={3}>
          Completa cuántos bultos enviarás:
        </Typography>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: '1.5rem',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                <Typography variant="body1" fontWeight="bold">
                  Bultos preparados
                </Typography>
                <TextField
                  variant="standard"
                  {...register('parcels')}
                  placeholder="Bultos"
                  error={!!errors?.parcels}
                  helperText={errors?.parcels?.message}
                  sx={{ width: '80%', '&& input': { textAlign: 'center' } }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" fontWeight="bold">
                Cada etiqueta de 99minutos tiene un código único. No la dupliques, ni pegues la
                misma en diferentes bultos.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Box mt={2}>
        <Typography variant="subtitle2" mb={3}>
          Teléfono de la persona responsable del envío:
        </Typography>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: '1.5rem',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                <Typography variant="body1" fontWeight="bold">
                  Teléfono del contacto
                </Typography>
                <Controller
                  name="contactPhone"
                  control={control}
                  render={({ field }) => (
                    <MuiTelInput
                      sx={{
                        width: '100%',
                        '& .MuiInputBase-root': {
                          textAlign: 'center',
                        },
                      }}
                      onlyCountries={ALLOWED_COUNTRIES}
                      defaultCountry={defaultCountryPhone || DEFAULT_COUNTRY}
                      error={!!errors?.contactPhone}
                      helperText={errors?.contactPhone?.message}
                      {...field}
                      onChange={(value, country) => {
                        const newCountry = (country.countryCode as CountryCode) || DEFAULT_COUNTRY;

                        const maxLength =
                          MAX_PHONE_LENGTH_BY_COUNTRY[newCountry] ||
                          MAX_PHONE_LENGTH_BY_COUNTRY[DEFAULT_COUNTRY];
                        const cleanValue = value.replace(/\s/g, '');

                        // Limitar la longitud y actualizar siempre
                        const finalValue =
                          cleanValue.length <= maxLength
                            ? cleanValue
                            : cleanValue.substring(0, maxLength);

                        field.onChange(finalValue);

                        if (handleChangeSelectedCountryPhone) {
                          handleChangeSelectedCountryPhone(newCountry);
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" fontWeight="bold">
                Este número será usado para coordinar la recepción y cualquier consulta sobre el
                envío.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {requiresHomePickup && (
        <Box mt={2}>
          <Typography variant="subtitle2" mb={3}>
            Tipo de envío a bodega:
          </Typography>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: '1.5rem',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <Typography variant="body1" fontWeight="bold">
                    Tipo de envío
                  </Typography>
                  <Controller
                    name="shipmentType"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label="Selecciona el tipo"
                        error={!!errors?.shipmentType}
                        helperText={errors?.shipmentType?.message}
                      >
                        {SHIPMENT_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" fontWeight="bold">
                  Define cómo serán transportados tus productos a la bodega. (Sprint) para envíos
                  rápidos, (Same Day) para entrega el mismo día.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      <Grid
        container
        mt={4}
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        direction={{ xs: 'column-reverse', md: 'row' }}
      >
        <Grid>
          <Button variant="outlined" onClick={() => setCurrentStep(step - 1)}>
            Atrás
          </Button>
        </Grid>
        <Grid>
          <Button
            disabled={isDisabled}
            onClick={() => setCurrentStep(editStep ? (requiresHomePickup ? 5 : 4) : step + 1)}
          >
            Continuar
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LabelYourProducts;
